import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api, parseErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";

const CENTS = (c) => (c == null ? "—" : `$${(Math.abs(c) / 100).toFixed(2)}${c < 0 ? "" : ""}`);

// R7 policy — mirror of server constants.
const REWARD_THRESHOLD_CENTS = 400000;
const EARN_NUM = 3;
const EARN_DEN = 200;
function computeEarnPreview(productExGstCents) {
  const base = Math.max(0, Number(productExGstCents || 0));
  if (base < REWARD_THRESHOLD_CENTS) return { eligible: false, cents: 0 };
  // half-up rounding, integer cents
  const raw = (base * EARN_NUM) / EARN_DEN;
  const cents = Math.floor(raw + 0.5);
  return { eligible: true, cents };
}

export default function OrderSummary() {
  const nav = useNavigate();
  const { cart, asLines, clear } = useCart();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [paymentTerm, setPaymentTerm] = useState("COD"); // R7 default
  const [useReward, setUseReward] = useState(false);      // R8 — default OFF
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // R9: if the buyer arrived via "Edit order" on OrderSuccess, this holds the
  // original order id — Submit becomes an atomic edit-replace instead of a
  // fresh submission.
  const [replacesOrderId, setReplacesOrderId] = useState(null);
  const [replacesOrderNumber, setReplacesOrderNumber] = useState(null);
  useEffect(() => {
    try {
      const id = sessionStorage.getItem("apwo_edit_replaces_order_id");
      const num = sessionStorage.getItem("apwo_edit_replaces_order_number");
      if (id) {
        setReplacesOrderId(id);
        setReplacesOrderNumber(num || null);
      }
    } catch (_) {}
  }, []);
  const clearEditContext = () => {
    try {
      sessionStorage.removeItem("apwo_edit_replaces_order_id");
      sessionStorage.removeItem("apwo_edit_replaces_order_number");
    } catch (_) {}
  };

  const lines = useMemo(() => asLines(), [asLines, cart]);

  const requestQuote = useCallback(async () => {
    if (lines.length === 0) { setQuote(null); return; }
    setLoading(true); setError("");
    try {
      const r = await api.post("/quotes/prepare", { lines, use_reward: useReward });
      setQuote(r.data);
    } catch (err) {
      setQuote(null);
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [lines, useReward]);

  useEffect(() => { requestQuote(); }, [requestQuote]);

  const submitOrder = useCallback(async () => {
    if (!quote?.quote_id || submitting) return;
    setSubmitting(true); setSubmitError("");
    try {
      let r;
      if (replacesOrderId) {
        r = await api.post(`/orders/${replacesOrderId}/edit-replace`, {
          quote_id: quote.quote_id,
          payment_term: paymentTerm,
          use_reward: useReward,
          reason: null,
          customer_note: (note || "").trim() || null,
        });
        clearEditContext();
      } else {
        r = await api.post("/orders/submit", {
          quote_id: quote.quote_id,
          payment_term: paymentTerm,
          use_reward: useReward,
          customer_note: (note || "").trim() || null,
        });
      }
      clear();
      nav(`/portal/order/success/${r.data.order_id}`, { replace: true });
    } catch (err) {
      setSubmitError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  }, [quote, note, paymentTerm, useReward, submitting, nav, clear, replacesOrderId]);

  if (lines.length === 0) {
    return (
      <PortalLayout title="Order Summary">
        <div className="apwo-panel apwo-empty-panel" data-testid="summary-empty">
          <h3>Your cart is empty.</h3>
          <p className="apwo-muted">Add products from Core or Premium &amp; Special to see a live quote.</p>
          <div className="apwo-empty-actions">
            <button className="apwo-btn-secondary" onClick={() => nav("/portal/core")} data-testid="summary-goto-core">Go to Core Products</button>
            <button className="apwo-btn-secondary" onClick={() => nav("/portal/premium")} data-testid="summary-goto-ps">Go to Premium &amp; Special</button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  const money = quote?.money;
  const coreLines = (quote?.lines || []).filter((l) => l.category === "CORE");
  const psLines = (quote?.lines || []).filter((l) => l.category === "PREMIUM_SPECIAL");
  const canSubmit = !!quote?.quote_id && !loading && !submitting && !error;

  return (
    <PortalLayout title="Order Summary">
      <div className="apwo-summary-grid">
        <div className="apwo-panel apwo-panel--summary" data-testid="summary-lines">
          <div className="apwo-summary-head">
            <h2>Order Summary</h2>
            <button className="apwo-linky" onClick={() => nav("/portal/core")} data-testid="summary-edit">Edit Order</button>
          </div>

          {loading && <p className="apwo-muted">Refreshing server quote…</p>}
          {error && <div className="apwo-alert-error" data-testid="summary-error">{error}</div>}

          {coreLines.length > 0 && (
            <>
              <h3 className="apwo-summary-section">Core Products</h3>
              <table className="apwo-summary-table">
                <thead>
                  <tr><th>Product</th><th>Specifications</th><th className="apwo-th-right">Qty</th><th className="apwo-th-right">Unit price (ex GST)</th><th className="apwo-th-right">Line total (ex GST)</th></tr>
                </thead>
                <tbody>
                  {coreLines.map((l) => (
                    <tr key={l.product_id} data-testid={`summary-core-${l.product_id}`}>
                      <td>{l.display_name}</td>
                      <td className="apwo-cell-muted">{l.tier === "standard" ? "1–9 std" : l.tier === "ten_plus" ? "10–47 support" : "48+ pallet best"}</td>
                      <td className="apwo-th-right">{l.qty}</td>
                      <td className="apwo-th-right">{CENTS(l.unit_price_cents)}</td>
                      <td className="apwo-th-right">{CENTS(l.line_total_ex_gst_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {psLines.length > 0 && (
            <>
              <h3 className="apwo-summary-section">Premium &amp; Special Products</h3>
              <table className="apwo-summary-table">
                <thead>
                  <tr><th>Product</th><th>Tier</th><th className="apwo-th-right">Qty</th><th className="apwo-th-right">Unit price (ex GST)</th><th className="apwo-th-right">Line total (ex GST)</th></tr>
                </thead>
                <tbody>
                  {psLines.map((l) => (
                    <tr key={l.product_id} data-testid={`summary-ps-${l.product_id}`}>
                      <td>{l.display_name}</td>
                      <td className="apwo-cell-muted">{l.tier === "mixed_twenty" ? "Mixed 20+ support" : "Standard"}</td>
                      <td className="apwo-th-right">{l.qty}</td>
                      <td className="apwo-th-right">{CENTS(l.unit_price_cents)}</td>
                      <td className="apwo-th-right">{CENTS(l.line_total_ex_gst_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="apwo-summary-note" data-testid="summary-ps-combined">
                Combined Premium &amp; Special qty: <strong>{quote?.ps_combined_qty}</strong> — tier applied: <strong>{quote?.ps_tier_active === "mixed_twenty" ? "Mixed 20+ support" : "Standard"}</strong>.
              </div>
            </>
          )}

          <div className="apwo-note-block" data-testid="summary-note-block">
            <label className="apwo-note-label" htmlFor="apwo-customer-note">
              Order note <span className="apwo-cell-muted">(optional, up to 500 characters)</span>
            </label>
            <textarea
              id="apwo-customer-note"
              className="apwo-note-textarea"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the warehouse team should know about this request (e.g. delivery preferences, timing, PO reference)."
              data-testid="customer-note-input"
            />
            <div className="apwo-note-counter" data-testid="customer-note-counter">
              {note.length}/500
            </div>
          </div>
        </div>

        <aside className="apwo-panel apwo-panel--totals" data-testid="summary-totals">
          <h3 className="apwo-panel-heading">Order Totals</h3>

          <div className="apwo-totals-row">
            <span>Product subtotal (ex GST)</span>
            <span data-testid="tot-product-ex">{CENTS(money?.product_subtotal_ex_gst_cents)}</span>
          </div>
          <div className="apwo-totals-row">
            <span>Freight (ex GST)</span>
            <span data-testid="tot-freight-ex">{CENTS(money?.freight_ex_gst_cents)}</span>
          </div>
          <div className="apwo-totals-row">
            <span>Taxable (ex GST)</span>
            <span data-testid="tot-taxable-ex">{CENTS(money?.taxable_ex_gst_cents)}</span>
          </div>
          <div className="apwo-totals-row">
            <span>GST (10%)</span>
            <span data-testid="tot-gst">{CENTS(money?.gst_total_cents)}</span>
          </div>
          <div className="apwo-totals-row apwo-totals-row--total">
            <span>TOTAL PAYABLE (incl. GST)</span>
            <span data-testid="tot-payable">{CENTS(money?.payable_total_cents)}</span>
          </div>

          <div className="apwo-reward-block" data-testid="reward-panel">
            {(() => {
              const available = Number(quote?.available_reward_cents || 0);
              const applied   = Number(money?.reward_applied_cents || 0);
              const remaining = Number(money?.payable_total_cents || 0);
              const hasBalance = available > 0;
              return (
                <>
                  <p className="apwo-reward-avail" data-testid="reward-available">
                    Available Reward: <strong>{CENTS(available)}</strong>
                  </p>
                  <label className={`apwo-reward-toggle ${!hasBalance ? "apwo-reward-toggle--disabled" : ""}`}>
                    <input
                      type="checkbox"
                      disabled={!hasBalance}
                      checked={useReward && hasBalance}
                      onChange={(e) => setUseReward(e.target.checked)}
                      data-testid="reward-toggle"
                    />
                    <span>Use available Reward</span>
                  </label>
                  {!hasBalance && (
                    <p className="apwo-reward-none" data-testid="reward-none">
                      No Reward is currently available.
                    </p>
                  )}
                  {useReward && hasBalance && applied > 0 && (
                    <div className="apwo-reward-applied" data-testid="reward-applied-block">
                      <div className="apwo-totals-row">
                        <span>Reward applied</span>
                        <span data-testid="reward-applied-cents">-{CENTS(applied)}</span>
                      </div>
                      <div className="apwo-totals-row apwo-totals-row--total">
                        <span>Remaining payable</span>
                        <span data-testid="reward-remaining-payable">{CENTS(remaining)}</span>
                      </div>
                      <p className="apwo-fineprint" data-testid="reward-remaining-note">
                        Any unused Reward balance remains available.
                      </p>
                    </div>
                  )}
                  <p className="apwo-fineprint" data-testid="reward-expiry-note">
                    Reward credits expire 180 days after they are earned.
                    Reward tax policy: <strong>{quote?.reward_tax_policy_version || "D8_GST_INCLUSIVE_DISCOUNT_V1"}</strong>.
                  </p>
                </>
              );
            })()}
          </div>

          <div className="apwo-ship-block" data-testid="summary-ship">
            <p className="apwo-ship-title">Delivery address (fixed)</p>
            <address className="apwo-ship-address">
              AP WAREHOUSE PTY LTD<br/>
              17–19 Anzac St, Greenacre NSW 2190<br/>
              ABN 83 144 869 518
            </address>
            <p className="apwo-fineprint">
              To change the delivery address, contact AP Warehouse Team (order@autopartsb2b.com.au / 0426 266 655).
            </p>
            <p className="apwo-fineprint">Lead time: 4–10 working days, subject to logistics conditions.</p>
          </div>

          <div className="apwo-pay-block" data-testid="summary-pay">
            <p className="apwo-pay-title">Payment term</p>
            <label
              className={`apwo-payterm-option ${paymentTerm === "COD" ? "apwo-payterm-option--active" : ""}`}
              data-testid="payterm-cod-card"
              data-active={paymentTerm === "COD" ? "true" : "false"}
            >
              <input
                type="radio"
                name="apwo-payment-term"
                value="COD"
                checked={paymentTerm === "COD"}
                onChange={() => setPaymentTerm("COD")}
                data-testid="payterm-cod-radio"
              />
              <div className="apwo-payterm-body">
                <span className="apwo-payterm-label">COD — No Reward</span>
                <span className="apwo-payterm-sub">Pay on delivery. No Reward is earned.</span>
              </div>
            </label>

            <label
              className={`apwo-payterm-option ${paymentTerm === "PREPAY" ? "apwo-payterm-option--active" : ""}`}
              data-testid="payterm-prepay-card"
              data-active={paymentTerm === "PREPAY" ? "true" : "false"}
            >
              <input
                type="radio"
                name="apwo-payment-term"
                value="PREPAY"
                checked={paymentTerm === "PREPAY"}
                onChange={() => setPaymentTerm("PREPAY")}
                data-testid="payterm-prepay-radio"
              />
              <div className="apwo-payterm-body">
                <span className="apwo-payterm-label">Prepay — Earn 1.5% Reward</span>
                <span className="apwo-payterm-sub">Pay before dispatch. Eligible orders earn 1.5% Reward after cleared payment is confirmed.</span>
              </div>
            </label>
            <p className="apwo-fineprint apwo-payterm-rules" data-testid="payterm-rules">
              Minimum eligible product value: $4,000 ex GST. GST and freight are excluded. Reward is available for future orders only and cannot be used on this order.
            </p>

            {paymentTerm === "PREPAY" && quote && (() => {
              const est = computeEarnPreview(money?.product_subtotal_ex_gst_cents);
              return est.eligible ? (
                <p className="apwo-payterm-est apwo-payterm-est--ok" data-testid="payterm-est-ok">
                  Estimated Reward: {CENTS(est.cents)} — credited after cleared payment is confirmed.
                </p>
              ) : (
                <p className="apwo-payterm-est apwo-payterm-est--below" data-testid="payterm-est-below">
                  This order is below the $4,000 ex GST Reward threshold and will earn $0 Reward.
                </p>
              );
            })()}
          </div>

          <div className="apwo-submit-block" data-testid="submit-order-block">
            {submitError && (
              <div className="apwo-alert-error" data-testid="submit-order-error">{submitError}</div>
            )}
            <button
              className="apwo-btn-primary apwo-btn-primary--submit"
              onClick={submitOrder}
              disabled={!canSubmit}
              aria-disabled={!canSubmit}
              data-testid="submit-order-btn"
            >
              {submitting ? "Submitting…" : (replacesOrderId ? "Submit replacement order" : "Submit Order Request")}
            </button>
            <p className="apwo-fineprint" data-testid="submit-order-note">
              {replacesOrderId
                ? `This will cancel your original order ${replacesOrderNumber || replacesOrderId} and submit this as the replacement (atomic).`
                : "Availability will be confirmed and Prepay payment instructions sent separately. The official Xero tax invoice is emailed after cleared payment is confirmed."}
            </p>
          </div>
        </aside>
      </div>
    </PortalLayout>
  );
}
